import { useState } from "react";
import { api } from "../api/client";
import { ErrorBox } from "../components/States";
import { MeResponse } from "../types";

interface CloseoutResp {
  period: [string, string];
  snapshot: string | null;
  deals_recomputed: number;
  consumptions_removed?: number;
  consumptions_created?: number;
  reports: string[];
  errors: { deal_id?: number; error?: string }[];
  notified_documents?: number;
}

export function CloseoutPage({ me }: { me: MeResponse }) {
  // По умолчанию — прошлый месяц.
  const today = new Date();
  const prevStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const prevEnd = new Date(today.getFullYear(), today.getMonth(), 0);
  const iso = (d: Date) => d.toISOString().slice(0, 10);

  const [start, setStart] = useState<string>(iso(prevStart));
  const [end, setEnd] = useState<string>(iso(prevEnd));
  const [notify, setNotify] = useState<boolean>(true);
  const [res, setRes] = useState<CloseoutResp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<boolean>(false);

  if (!me.roles.includes("admin")) {
    return (
      <div className="text-tg-hint text-sm">
        Закрытие периода доступно только администратору.
      </div>
    );
  }

  const run = async () => {
    if (!confirm(
      `Закрыть период ${start} — ${end}?\n\n` +
      "Будет сделан снапшот БД, пересобрана FIFO-привязка лотов к " +
      "импортным заявкам, сформирован комплект отчётов." +
      (notify ? "\nОтчёты будут поставлены в очередь рассылки админам через бот." : ""),
    )) return;
    setBusy(true);
    setError(null);
    setRes(null);
    try {
      const r = await api.post<CloseoutResp>("/api/closeout/recompute", {
        period_start: start,
        period_end: end,
        notify,
      });
      setRes(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <h1 className="text-xl font-semibold">📅 Закрытие периода</h1>
      <p className="text-xs text-tg-hint">
        Снапшот БД → FIFO-перепривязка лотов по фактическим датам платежей
        → формирование board_report + fx_pnl + client_cost → постановка
        в очередь рассылки админам через бот.
      </p>

      <section className="bg-tg-sectionBg rounded-xl p-3 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs text-tg-hint">
            Начало периода
            <input
              type="date" value={start}
              onChange={(e) => setStart(e.target.value)}
              className="block w-full mt-0.5 px-2 py-1.5 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
            />
          </label>
          <label className="text-xs text-tg-hint">
            Конец периода
            <input
              type="date" value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="block w-full mt-0.5 px-2 py-1.5 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
            />
          </label>
        </div>
        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox" checked={notify}
            onChange={(e) => setNotify(e.target.checked)}
          />
          Отправить отчёты админам через бот
        </label>
        <button
          disabled={busy || !start || !end}
          onClick={run}
          className="w-full py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium disabled:opacity-50"
        >
          {busy ? "Закрытие…" : "🔒 Закрыть период"}
        </button>
      </section>

      {error && <ErrorBox text={error} />}

      {res && (
        <section className="bg-tg-sectionBg rounded-xl p-3 space-y-2">
          <div className="text-sm font-medium">
            Результат: {res.period[0]} — {res.period[1]}
          </div>
          <div className="text-xs space-y-1">
            <div>📦 Снапшот: <span className="text-tg-hint">{res.snapshot || "—"}</span></div>
            <div>♻️ Пересчитано заявок: <strong>{res.deals_recomputed}</strong></div>
            {res.consumptions_removed !== undefined && (
              <div>
                Удалено старых распределений: {res.consumptions_removed}, создано
                новых: {res.consumptions_created}
              </div>
            )}
            <div>📥 Отчёты сформированы: {res.reports.length}</div>
            {res.notified_documents !== undefined && (
              <div>📨 В очереди рассылки: {res.notified_documents}</div>
            )}
            {res.errors.length > 0 && (
              <div className="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-2 py-1 rounded">
                <div className="font-medium mb-1">Ошибки: {res.errors.length}</div>
                <ul className="space-y-0.5">
                  {res.errors.slice(0, 10).map((e, i) => (
                    <li key={i}>
                      {e.deal_id ? `D-${String(e.deal_id).padStart(6, "0")}: ` : ""}
                      {e.error || JSON.stringify(e)}
                    </li>
                  ))}
                  {res.errors.length > 10 && (
                    <li>…и ещё {res.errors.length - 10}</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
